import React from "react";
import { createPortal } from "react-dom";
import Icon from "@mdi/react";
import {
  mdiCheckCircleOutline,
  mdiClose,
  mdiCloseCircleOutline,
} from "@mdi/js";
import { WarehouseOutwardModel } from "../model/warehouse_outward_model";

type Props = {
  open: boolean;
  outward: WarehouseOutwardModel | null;
  approving?: boolean;
  cancelling?: boolean;
  rejecting?: boolean;
  onClose: () => void;
  onApprove: () => void;
  onCancelST: () => void;
  onReject: () => void;
};

const ApproveOrCancelSTFGModal: React.FC<Props> = ({
  open,
  outward,
  approving,
  cancelling,
  rejecting,
  onClose,
  onApprove,
  onCancelST,
  onReject,
}) => {
  if (!open || !outward) return null;

  const busy = !!approving || !!cancelling || !!rejecting;

  return createPortal(
    <div
      id="warehouse-outward-approve-cancel-stfg-modal"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 p-4 overflow-y-auto"
      style={{ top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div
        className="relative w-full max-w-lg my-auto flex flex-col rounded-xl bg-white shadow-xl overflow-hidden wh-modal-xs"
        style={{ maxHeight: "90vh" }}
      >
        <div className="flex items-center justify-between border-b px-6 py-4 shrink-0">
          <div>
            <h2 className="text-lg font-semibold">
              Approve FG for stock transfer {outward.id}?
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              It will move to "Device Allocation Pending".
            </p>
          </div>
          <button
            id="warehouse-outward-approve-cancel-stfg-close-icon-btn"
            onClick={onClose}
            disabled={busy}
            className={`rounded-full p-1 ${
              busy ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100"
            }`}
          >
            <Icon path={mdiClose} size={0.9} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-3">
          <button
            id="warehouse-outward-approve-stfg-btn"
            type="button"
            onClick={onApprove}
            disabled={busy}
            className={`w-full text-left rounded-lg border p-4 transition-colors ${
              busy
                ? "opacity-50 cursor-not-allowed"
                : "hover:border-emerald-500 hover:bg-emerald-50"
            }`}
          >
            <div className="flex items-start gap-3">
              <Icon
                path={mdiCheckCircleOutline}
                size={1}
                className="text-emerald-600 mt-0.5 shrink-0"
              />
              <div>
                <p className="font-semibold text-gray-900">
                  {approving ? "Approving..." : "Approve FG"}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {`Approve FG for stock transfer ${outward.id}? It will move to "Device Allocation Pending".`}
                </p>
              </div>
            </div>
          </button>

          <button
            id="warehouse-outward-cancel-stfg-btn"
            type="button"
            onClick={onCancelST}
            disabled={busy}
            className={`w-full text-left rounded-lg border p-4 transition-colors ${
              busy
                ? "opacity-50 cursor-not-allowed"
                : "hover:border-red-500 hover:bg-red-50"
            }`}
          >
            <div className="flex items-start gap-3">
              <Icon
                path={mdiCloseCircleOutline}
                size={1}
                className="text-red-600 mt-0.5 shrink-0"
              />
              <div>
                <p className="font-semibold text-gray-900">
                  {cancelling ? "Cancelling..." : "Cancel stock transfer"}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Cancels this stock transfer. Allocated device movements and
                  in-transit entries will be removed.
                </p>
              </div>
            </div>
          </button>

          <button
            id="warehouse-outward-reject-stfg-btn"
            type="button"
            onClick={onReject}
            disabled={busy}
            className={`w-full text-left rounded-lg border p-4 transition-colors ${
              busy
                ? "opacity-50 cursor-not-allowed"
                : "hover:border-amber-500 hover:bg-amber-50"
            }`}
          >
            <div className="flex items-start gap-3">
              <Icon
                path={mdiCloseCircleOutline}
                size={1}
                className="text-amber-600 mt-0.5 shrink-0"
              />
              <div>
                <p className="font-semibold text-gray-900">
                  {rejecting ? "Rejecting..." : "Reject stock transfer"}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Sends this stock transfer back for editing. You'll be asked
                  for a rejection reason.
                </p>
              </div>
            </div>
          </button>
        </div>

        <div className="border-t px-6 py-3 shrink-0 flex justify-end">
          <button
            id="warehouse-outward-approve-cancel-stfg-footer-close-btn"
            onClick={onClose}
            disabled={busy}
            className={`h-9 px-4 rounded-md border border-gray-300 text-sm ${
              busy
                ? "opacity-50 cursor-not-allowed"
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ApproveOrCancelSTFGModal;
